"use client";
import type { Resolver } from "react-hook-form";

import React from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import moment from "moment";
import { ProgramScheduleType } from "../../../../types/dto/program";
import { programPaymentScheduleAcademy } from "../../../../shared/services";
import { message } from "antd";
import { resolveScheduleMonthDate } from "../../utils/scheduleDateUtils";

export interface UseUpdateProgramsScheduleProps {
  programSchedule: ProgramScheduleType;
  onSuccess?: () => void;
  setOpenModal: (value: boolean) => void;
}

export default function useUpdateProgramsSchedule({
  onSuccess,
  programSchedule,
  setOpenModal,
}: UseUpdateProgramsScheduleProps) {
  const [loading, setLoading] = React.useState(false);

  const schema = yup.object({
    day_of_month_payment_limit: yup.string().required("Campo Requerido"),
    price: yup.string().required("Campo Requerido"),
    price_with_prompt_payment_discount: yup
      .string()
      .required("Campo Requerido"),
  });

  const {
    control,
    handleSubmit: onSubmit,
    reset,
  } = useForm<ProgramScheduleType>({
    resolver: yupResolver(schema) as unknown as Resolver<ProgramScheduleType>,
  });

  const handleSubmit = onSubmit(async (data) => {
    if (loading) return;

    setLoading(true);

    const monthDate = resolveScheduleMonthDate(
      programSchedule.month,
      programSchedule.day_of_month_payment_limit
    );

    if (!monthDate.isValid()) {
      message.error("No se pudo determinar el mes de cobro del cronograma.");
      setLoading(false);
      return;
    }

    const dayLimit = moment(data.day_of_month_payment_limit).date();
    const paymentLimitDate = moment(monthDate).date(dayLimit);

    if (paymentLimitDate.month() !== monthDate.month()) {
      paymentLimitDate.subtract(1, "month");
    }

    try {
      await programPaymentScheduleAcademy.patch(programSchedule.id, {
        month: monthDate.format("YYYY-MM-DD"),
        day_of_month_payment_limit: paymentLimitDate.toDate(),
        price: +data.price,
        price_with_prompt_payment_discount:
          +data.price_with_prompt_payment_discount,
        program_id: +programSchedule.program_id,
      });
      message.success("Se ha editado con éxito el cronograma de pago");
      setOpenModal(false);
      onSuccess?.();
    } catch (err: any) {
      message.error(`Algo salió mal: ${err.message}`);
    } finally {
      setLoading(false);
    }
  });

  React.useEffect(() => {
    if (programSchedule) {
      reset({
        day_of_month_payment_limit: moment(
          programSchedule.day_of_month_payment_limit
        ).format("YYYY-MM-DD"),
        price: `${programSchedule.price ?? ""}`,
        price_with_prompt_payment_discount: `${
          programSchedule.price_with_prompt_payment_discount ?? ""
        }`,
      });
    }
  }, [programSchedule, reset]);

  return {
    control,
    loading,
    handleSubmit,
  };
}
